import React, {useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {QueryKey, useQuery, UseQueryOptions} from '@tanstack/react-query';
import app from '@src/helpers/app';
import CommentInline from '@src/components/CommentsList/CommentInline';
import {ICommentModel} from '@src/@interfaces/models/Comment/ICommentModel';
import {default as BaseCommentsList} from '@src/components/CommentsList/CommentsList';
import Translate from '@src/components/Translate/Translate';
import {FormFieldDesignContext} from '@src/contexts/FormFieldDesignContext';
import classnames from '@src/helpers/classnames';
import RenderIf from '@src/components/RenderIf/RenderIf';
import useWebSocket from '@src/hooks/useWebSocket';
import useDebounce from '@src/hooks/useDebounce';
import {plural} from 'pluralize';
import {classBasename} from '@src/helpers/classBasename';

function passThrough<T>(item: T): T {
    return item;
}

export default function CommentsList<TMessage extends ICommentModel>(props: {
    modelId?: string | number | null,
    queryCacheKey?: QueryKey,
    queryOptions?: Partial<Omit<UseQueryOptions<TMessage[] | null, Error>, 'queryKey' | 'queryFn'>>,
    controllerName: string,
    getCommentsRouteName: string,
    createCommentRouteName: string,
    size?: React.ComponentProps<typeof BaseCommentsList>['size'],
    isDisabled?: boolean,
    prepareComments?: (comments: TMessage[] | null) => TMessage[] | null,
    extraRequestRouteUrlParams?: Record<string, string>,
    isLazy?: boolean,
    shouldLoadNow?: boolean,
    maxHeight?: number,
    intersectionObserverRoot?: React.ComponentProps<typeof BaseCommentsList>['intersectionObserverRoot'],
    label?: string,
    labelSize?: 'sm' | 'lg',
    labelClassName?: string,
    listenToModelName?: string,
    listenToModelId?: string | number,
}) {
    const [design] = useContext(FormFieldDesignContext);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [commentsToRender, setCommentsToRender] = useState<TMessage[]>([]);

    const queryKey = useMemo(() => {
        return app('queryKeys').getCustomRequestQueryKey(props.queryCacheKey ?? {
            controllerName: props.controllerName,
            modelId: props.modelId,
            getCommentsRouteName: props.getCommentsRouteName,
            extraRequestRouteUrlParams: props.extraRequestRouteUrlParams,
            isLazy: props.isLazy,
            queryOptions: props.queryOptions,
        });
    }, [props.controllerName, props.extraRequestRouteUrlParams, props.getCommentsRouteName, props.isLazy, props.modelId, props.queryCacheKey, props.queryOptions]);

    const {data, error, isFetching, refetch} = useQuery<TMessage[] | null, Error>(queryKey, () => {
        if (!props.modelId) {
            return Promise.resolve(null);
        }

        return app('http').route(props.getCommentsRouteName, {
            id: String(props.modelId),
            ...(props.extraRequestRouteUrlParams ?? {}),
        }, {
            body: {['controller-name']: props.controllerName}
        });
    }, {
        useErrorBoundary: false,
        enabled: !props.isLazy || props.shouldLoadNow === true,
        ...(props.queryOptions ?? {}),
    });

    const listenToModelName = (props.listenToModelName ? plural(props.listenToModelName) : null) ?? props.controllerName;
    const listenToModelId = props.listenToModelId ?? props.modelId;
    const {lastEventFiredAt} = useWebSocket<{
        commentable_id: string | number,
        commentable_type: string,
        model: string,
        id: string | number,
        is_just_touch: boolean,
        performed_by: string | number | null,
    }>({
        model: 'comment',
        event: [
            'CommentCreated',
            'CommentUpdated',
            'CommentDeleted',
            'CommentTrashed',
            'CommentForceDeleted',
            'CommentRestored',
        ],
        filterMessages: (event, payload) => {
            return (
                    plural(classBasename(payload.commentable_type)) === listenToModelName &&
                    payload.commentable_id === listenToModelId
            );
        },
        enabledOnlyWhenAuthorized: true,
    });
    const debouncedLastEventFiredAt = useDebounce(lastEventFiredAt, 500);
    useEffect(() => {
        if (debouncedLastEventFiredAt) {
            refetch().then();
        }
    }, [debouncedLastEventFiredAt, refetch]);

    const prepareList = props.prepareComments ?? passThrough;
    const [rawComments, setRawComments] = useState<TMessage[] | null>(null);

    useEffect(() => {
        if (error) {
            setErrorMessage(error.message);
        }
    }, [error]);

    useEffect(() => {
        setRawComments(data ?? null);
    }, [data]);

    useEffect(() => {
        const comments = prepareList(rawComments ?? null);
        const sortedComments = (comments ?? []).sort((a, b) => b.id - a.id);

        setCommentsToRender(sortedComments);
    }, [prepareList, rawComments]);

    type TCallback = NonNullable<React.ComponentProps<typeof BaseCommentsList>['onPostMessage']>;
    const onSubmit = useCallback<TCallback>((message, attachments, answerTo) => {
        setErrorMessage(null);

        if (!message?.length && !attachments.length) {
            return Promise.resolve(false).then(() => {
                setErrorMessage('Chat:MessageReceiver.message-or-attachment-is-required');

                return false;
            });
        }

        return app('http').route(props.createCommentRouteName, {
            id: String(props.modelId ?? 'create'),
            ...(props.extraRequestRouteUrlParams ?? {}),
        }, {
            headers: {'content-type': 'multipart/form-data'},
            body: {'controller-name': props.controllerName, message, attachments, answerTo}
        }).then(comment => {
            setRawComments(list => [comment, ...(list ?? [])]);

            return true;
        });
    }, [props.controllerName, props.createCommentRouteName, props.extraRequestRouteUrlParams, props.modelId]);

    const {label, labelSize} = props;

    return <>
        <RenderIf expression={label}>
            <Translate
                    tag="label"
                    message={label as string}
                    className={classnames(props.labelClassName, 'col-form-label', {
                        [`col-form-label-${labelSize ?? design.size}`]: labelSize ?? design.size,
                    })}
            />
        </RenderIf>
        <BaseCommentsList
                size={props.size}
                list={commentsToRender}
                isLoading={isFetching}
                isDisabled={props.isDisabled}
                itemComponent={CommentInline}
                leaveMessageFormPosition="top"
                onPostMessage={onSubmit}
                isInvalid={errorMessage != null}
                errorMessage={errorMessage}
                maxHeight={props.maxHeight}
                intersectionObserverRoot={props.intersectionObserverRoot}
                allowToReply
        />
    </>;
}
